import { designVariables } from "@studio3t/design-variables";
import {
  GenericInput,
  InputType,
  StyledTableScroll,
} from "@studio3t/generic-components";
import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDropdownDismissGuard } from "@/components/dropdownDismissGuard";
import {
  ActionsCell,
  CellText,
  EmptyRowMessage,
  Table,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ListTable";
import { PipelineFlowCell } from "@/components/PipelineFlowCell";
import { SkeletonBar } from "@/components/Skeleton";
import { SortableHeader } from "@/components/SortableHeader";
import {
  FilterBar,
  FilterControls,
  FilterInputWrap,
  FilterMultiSelect,
} from "@/components/TableFilters";
import {
  DEFAULT_PAGE_SIZES,
  TablePagination,
} from "@/components/TablePagination";
import type { JobMetadata, Workspace } from "@/types/api";
import { studioWorkspacePath } from "@/utils/studioRoute";
import { userStorage } from "@/utils/userStorage";
import {
  DRAFT_SORT_COLUMNS,
  type DraftCheckpoint,
  type DraftFilters,
  type DraftRow,
  type DraftSort,
  type DraftSortColumn,
  draftRows,
  draftSortDirection,
  draftWorkspaces,
  nextDraftSort,
  parseDraftSort,
  visibleDrafts,
} from "./pipelineDrafts";
import {
  UnresolvedPipelineBadge,
  useServiceNames,
  useWorkspaceMutations,
  WorkspaceActions,
  WorkspaceModals,
  type WorkspaceMutations,
} from "./workspaceRows";

const TABLE = designVariables.ui.table;

// The Pipelines page's Drafts tab (Figma TablePipeline 14894-157315): the
// Deployed table's chrome with the draft's columns — Name, the pipeline
// flow, Checkpoint, actions — and a name + checkpoint filter bar. A row
// opens the draft in the Studio; the "…" menu carries the workspace
// actions (rename, duplicate, delete).

const CHECKPOINT_OPTIONS: { value: DraftCheckpoint; label: string }[] = [
  { value: "enabled", label: "Enabled" },
  { value: "off", label: "Off" },
];

// Filter, sort and page-size settings survive a page refresh (per user),
// like the Deployed table's.
const TABLE_SETTINGS_KEY = "pipeline-drafts-table";

interface StoredSettings {
  name: string;
  checkpoint: DraftCheckpoint[];
  sort: DraftSort | null;
  pageSize: number;
}

const DEFAULT_SETTINGS: StoredSettings = {
  name: "",
  checkpoint: [],
  sort: null,
  pageSize: DEFAULT_PAGE_SIZES[0],
};

function loadSettings(): StoredSettings {
  try {
    const raw = userStorage.getItem(TABLE_SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const stored = JSON.parse(raw) as Partial<
      Record<keyof StoredSettings, unknown>
    >;
    return {
      name: typeof stored.name === "string" ? stored.name : "",
      checkpoint: checkpointValues(stored.checkpoint),
      sort: parseDraftSort(stored.sort),
      pageSize: DEFAULT_PAGE_SIZES.includes(stored.pageSize as number)
        ? (stored.pageSize as number)
        : DEFAULT_PAGE_SIZES[0],
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

// Keeps only values that are still valid options (storage may hold stale
// data).
function checkpointValues(value: unknown): DraftCheckpoint[] {
  if (!Array.isArray(value)) return [];
  const allowed = CHECKPOINT_OPTIONS.map((o) => o.value);
  return value.filter((v): v is DraftCheckpoint => allowed.includes(v));
}

function useDraftSettings() {
  const [settings, setSettings] = useState(loadSettings);
  useEffect(() => {
    userStorage.setItem(TABLE_SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);
  const update = (patch: Partial<StoredSettings>) =>
    setSettings((prev) => ({ ...prev, ...patch }));
  return { settings, update };
}

// Page index over the visible rows, kept in range when the row set shrinks
// underneath it (a filter, a deletion) — same rule as the Deployed table.
function usePage(total: number, pageSize: number) {
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
  }, [pageIndex, pageCount]);
  return { pageIndex, pageCount, setPageIndex };
}

export function DraftsTable({
  workspaces,
  jobs,
  loading,
}: {
  workspaces: Workspace[];
  jobs: JobMetadata[];
  // True while the workspaces query loads: skeleton rows instead of data.
  loading: boolean;
}) {
  const navigate = useNavigate();
  const mutations = useWorkspaceMutations();
  const serviceNames = useServiceNames();
  // A click that closes a row menu or the filter menu does nothing else.
  const dismissGuard = useDropdownDismissGuard<HTMLDivElement>();
  const { settings, update } = useDraftSettings();

  const rows = useMemo(
    () => draftRows(draftWorkspaces(workspaces, jobs), serviceNames),
    [workspaces, jobs, serviceNames],
  );
  const { name, checkpoint, sort } = settings;
  const filters: DraftFilters = { name, checkpoint };
  const visible = useMemo(
    () => visibleDrafts(rows, { name, checkpoint }, sort),
    [rows, name, checkpoint, sort],
  );
  const page = usePage(visible.length, settings.pageSize);
  const paged = visible.slice(
    page.pageIndex * settings.pageSize,
    (page.pageIndex + 1) * settings.pageSize,
  );
  const setFilter = (patch: Partial<DraftFilters>) => {
    update(patch);
    page.setPageIndex(0);
  };
  // Sorting reorders the whole set; back to the first page so the new top
  // rows are in view (same as the Deployed table).
  const toggleSort = (id: DraftSortColumn) => {
    update({ sort: nextDraftSort(sort, id) });
    page.setPageIndex(0);
  };

  return (
    <TableCard
      ref={dismissGuard.containerRef}
      onClickCapture={dismissGuard.onClickCapture}
    >
      <DraftsFilterBar
        filters={filters}
        onChange={setFilter}
        onMenuOpenChange={dismissGuard.onDropdownOpenChange}
      />

      <StyledTableScroll>
        <Table>
          {/* 240/120 mirror Figma's Table cell "middle width"/"min width"
              variables, which @studio3t/design-variables doesn't export
              yet (same note as the Deployed table). */}
          <colgroup>
            <col style={{ width: 240 }} />
            <col />
            <col style={{ width: 120 }} />
            <col style={{ width: TABLE.fixwidth }} />
          </colgroup>
          <DraftsTableHead sort={sort} onToggleSort={toggleSort} />
          <tbody>
            {loading && <SkeletonBodyRows />}
            {!loading &&
              paged.map((row) => (
                <DraftTableRow
                  key={row.workspace.id}
                  row={row}
                  mutations={mutations}
                  onOpen={(ws) => navigate(studioWorkspacePath(ws.id))}
                  onMenuOpenChange={dismissGuard.onDropdownOpenChange}
                />
              ))}
          </tbody>
        </Table>

        {!loading && visible.length === 0 && (
          <EmptyRowMessage>
            {rows.length === 0
              ? "You have no draft pipelines yet"
              : "No drafts match your filters"}
          </EmptyRowMessage>
        )}
      </StyledTableScroll>

      {/* Only page once there are more than a full page (10) of drafts —
          same threshold rule as the Deployed table. */}
      {visible.length > DEFAULT_PAGE_SIZES[0] && (
        <TablePagination
          pageIndex={page.pageIndex}
          pageCount={page.pageCount}
          pageSize={settings.pageSize}
          total={visible.length}
          onPageChange={page.setPageIndex}
          onPageSizeChange={(size) => {
            update({ pageSize: size });
            page.setPageIndex(0);
          }}
          onOpenChange={dismissGuard.onDropdownOpenChange}
        />
      )}

      <WorkspaceModals mutations={mutations} />
    </TableCard>
  );
}

const SORT_LABELS: Record<DraftSortColumn, string> = {
  name: "Name",
  pipeline: "Pipeline",
  checkpoint: "Checkpoint",
};

// Mirrors the column's sort state onto the <th> so assistive tech can
// announce whether (and how) a column is sorted.
function ariaSortFor(
  sort: DraftSort | null,
  id: DraftSortColumn,
): "ascending" | "descending" | "none" {
  const direction = draftSortDirection(sort, id);
  if (direction === "asc") return "ascending";
  if (direction === "desc") return "descending";
  return "none";
}

function DraftsTableHead({
  sort,
  onToggleSort,
}: {
  sort: DraftSort | null;
  onToggleSort: (id: DraftSortColumn) => void;
}) {
  return (
    <TableHead>
      <tr>
        {DRAFT_SORT_COLUMNS.map((id) => (
          <TableHeader key={id} aria-sort={ariaSortFor(sort, id)}>
            <SortableHeader
              direction={draftSortDirection(sort, id)}
              onClick={() => onToggleSort(id)}
            >
              {SORT_LABELS[id]}
            </SortableHeader>
          </TableHeader>
        ))}
        <TableHeader aria-label="Actions" />
      </tr>
    </TableHead>
  );
}

function DraftsFilterBar({
  filters,
  onChange,
  onMenuOpenChange,
}: {
  filters: DraftFilters;
  onChange: (patch: Partial<DraftFilters>) => void;
  onMenuOpenChange: (open: boolean) => void;
}) {
  return (
    <FilterBar>
      <FilterInputWrap>
        <GenericInput
          type={InputType.Text}
          value={filters.name}
          placeholder="Filter by name"
          onChange={(e) => onChange({ name: e.target.value })}
          allowClear={filters.name.length > 0}
          onClear={() => onChange({ name: "" })}
        />
      </FilterInputWrap>
      <FilterControls>
        <FilterMultiSelect
          placeholder="Checkpoint"
          values={filters.checkpoint}
          options={CHECKPOINT_OPTIONS}
          onChange={(checkpoint) => onChange({ checkpoint })}
          onOpenChange={onMenuOpenChange}
        />
      </FilterControls>
    </FilterBar>
  );
}

// A draft row: opens the draft in the Studio on click/Enter/Space, with the
// actions cell kept out of that navigation.
function DraftTableRow({
  row: { workspace, connector, unresolved, checkpoint },
  mutations,
  onOpen,
  onMenuOpenChange,
}: {
  row: DraftRow;
  mutations: WorkspaceMutations;
  onOpen: (ws: Workspace) => void;
  onMenuOpenChange: (open: boolean) => void;
}) {
  return (
    <TableRow
      tabIndex={0}
      onClick={() => onOpen(workspace)}
      onKeyDown={(e) => {
        // Keydown bubbles from focusable cell content (the actions dropdown
        // trigger); only act on the row itself.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(workspace);
        }
      }}
    >
      <TableCell>
        <CellText>{workspace.name}</CellText>
      </TableCell>
      <TableCell>
        {unresolved ? (
          <UnresolvedPipelineBadge />
        ) : (
          connector && <PipelineFlowCell job={{ pipeline: connector }} />
        )}
      </TableCell>
      <TableCell>
        <CellText>{checkpoint === "enabled" ? "Enabled" : "Off"}</CellText>
      </TableCell>
      <ActionsCell onClick={(e: ReactMouseEvent) => e.stopPropagation()}>
        <WorkspaceActions
          workspace={workspace}
          onOpen={onOpen}
          onRename={(ws) =>
            mutations.setNameModal({ kind: "rename", workspace: ws })
          }
          onDuplicate={(ws) =>
            mutations.setNameModal({ kind: "duplicate", workspace: ws })
          }
          onDelete={mutations.setPendingDelete}
          onMenuOpenChange={onMenuOpenChange}
        />
      </ActionsCell>
    </TableRow>
  );
}

// Skeleton body shown while the workspaces load; the bar shapes echo each
// column's content (name, flow, text, —).
const SKELETON_ROWS = Array.from({ length: 8 }, (_, i) => `skeleton-${i}`);

function SkeletonBodyRows() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <TableRow key={key} aria-hidden="true">
          <TableCell>
            <SkeletonBar $width="70%" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="55%" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="60%" />
          </TableCell>
          <ActionsCell />
        </TableRow>
      ))}
    </>
  );
}
